/**
 * EIS-1061: pure helpers for deriving "Find on Map" coordinates from a map
 * pin / practitioner record. Single source of truth for both the row gating
 * (should the option render?) and the coordinates the click handler uses, so
 * the rendered UI always matches what the handler can actually do.
 *
 * Three pin shapes exist:
 *  - Type 1 (DB practitioner) and Type 2 (DB lead): numeric `id`,
 *    coordinates live on the `addresses[]` entry matching `default_address`.
 *  - Type 3 (transient Google Places search result): string `id` (place_id),
 *    flat `latLon: [lat, lng]`, no `addresses`, no DB record.
 */

export type FindOnMapPin = {
  id?: number | string;
  latLon?: number[];
  addresses?: any[];
  default_address?: number | string;
} | null | undefined;

/** Type-3 transient Google Places pins carry a string place_id as their id. */
export const isTransientLocationPin = (pin: FindOnMapPin): boolean =>
  typeof pin?.id === 'string';

/**
 * Resolve the [lat, lon] that "Find on Map" should center on, or null when
 * the pin has no usable coordinates (the row must not render in that case).
 * Truthy coordinate checks intentionally match the legacy `isGeoValidated`
 * semantics (0 / null / undefined are treated as not geo-validated).
 */
export const resolveFindOnMapLatLon = (pin: FindOnMapPin): [number, number] | null => {
  if (!pin) return null;

  if (isTransientLocationPin(pin)) {
    const lat = pin.latLon?.[0];
    const lon = pin.latLon?.[1];
    return lat && lon ? [lat, lon] : null;
  }

  const defaultAddress = pin.addresses?.find((a: any) => a.id === pin.default_address);
  return defaultAddress?.latitude && defaultAddress?.longitude
    ? [defaultAddress.latitude, defaultAddress.longitude]
    : null;
};
